/**
 * Where a home's record came from, in words and dates for a page (Oct 2026, after the first
 * import of a supplier's file).
 *
 * The pages were written when every record was a scrape's. So the property table printed
 * "N/A" for the day an imported home was added (it read `dateAdded`, which only the
 * scrapers write), a home's own page labelled every record's creation "Scraped", and nothing
 * anywhere said which kind a record was. Lee, 9 Oct 2026: the table "doesn't seem to show if
 * properties are there because of a scrape or an import", and an imported home's page showed
 * "a 'scrape' of 9th october".
 *
 * WHICH kind a record is, is the one source rule's to say (models/propertySource.js): this
 * file only gives that answer its words, its date and its link. Do not test `jobId` or
 * `importId` here to decide a kind.
 */
import { sourceOf, SCRAPE, IMPORT, HUBSPOT, MANUAL } from '../models/propertySource';

/** One word for a table's row, and what happened, for a home's own page. */
export const ORIGIN_WORDS = Object.freeze({
  [SCRAPE]: Object.freeze({ word: 'Scrape', did: 'Scraped', long: 'Saved by a scrape' }),
  [IMPORT]: Object.freeze({ word: 'Import', did: 'Imported', long: "Brought in by an import of a supplier's file" }),
  [HUBSPOT]: Object.freeze({ word: 'HubSpot', did: 'Added from HubSpot', long: 'A quote in HubSpot' }),
  [MANUAL]: Object.freeze({ word: 'By hand', did: 'Added by hand', long: 'Added by hand' }),
});

/** An import's own page. For administrators: the functions behind it refuse anyone else. */
export const importHref = (importId) => `/dashboard/properties/import/${encodeURIComponent(String(importId))}`;

/** A stored time as a Date, or null: Firestore's Timestamp, its plain form, milliseconds, text, or a Date. */
export function asDate(value) {
  if (value === null || value === undefined || value === '') return null;
  let date = null;
  try {
    if (value instanceof Date) date = value;
    else if (typeof value.toDate === 'function') date = value.toDate();
    else if (typeof value === 'object' && typeof value._seconds === 'number') date = new Date(value._seconds * 1000);
    else if (typeof value === 'object' && typeof value.seconds === 'number') date = new Date(value.seconds * 1000);
    else if (typeof value === 'number' || typeof value === 'string') date = new Date(value);
  } catch (e) { return null; }
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

/** A day as the pages print one ("09 Oct 2026"), or null. A plain day written as text (2026-08-14) is that day wherever the browser is. */
export function dayOf(value) {
  const plain = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim()) : null;
  const date = plain ? asDate(new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))) : asDate(value);
  return date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
}

/**
 * Where a record came from.
 * @returns {{ kind, id, word, did, long, when: Date|null, href: string|null }}
 *   `when`: the moment it happened. For an import, when the import wrote it; otherwise when the record was made.
 *   `href`: the scrape's own page. An import's page is `importHref(id)`, and a page links to it only for an
 *   administrator: the kind alone does not say who may open it.
 */
export function originOf(property) {
  const p = property || {};
  const { kind, id } = sourceOf(p);
  const words = ORIGIN_WORDS[kind] || ORIGIN_WORDS[MANUAL];
  const when = kind === IMPORT ? (asDate(p.importedAt) || asDate(p.createdAt)) : asDate(p.createdAt);
  const href = kind === SCRAPE && id ? `/dashboard/properties/scrapes/${encodeURIComponent(String(id))}` : null;
  return { kind, id: id || null, word: words.word, did: words.did, long: words.long, when, href };
}

/**
 * The day a home was added, for a table's "Date Added": the scrapers' own field where there
 * is one; else the day an import brought it in; else the day the record was made.
 * (Before this, the cell read the scrapers' field alone and said "N/A" for everything else.)
 */
export function addedOn(property) {
  const p = property || {};
  return asDate(p.dateAdded) || asDate(p.importedAt) || asDate(p.createdAt);
}

const text = (v) => (typeof v === 'string' ? v.trim() : (typeof v === 'number' && Number.isFinite(v) ? String(v) : ''));

/**
 * What an import's file said of a home, for the home's own page. Until Oct 2026 these were
 * visible only in Raw data, which is for administrators.
 * @returns {null | { made: boolean, rows: {label, value}[], parts: {label, value}[] }}
 *   null when no import MADE this record (one an import only linked carries none of the file's fields).
 *   `rows`: the file's own facts. The UPRN is always among them, because "none in the file" is a fact too.
 *   `parts`: the address in parts, which is what the letter is printed from.
 */
export function fileFieldsOf(property) {
  const p = property || {};
  if (!p.importId) return null;
  const rows = [];
  rows.push({ label: 'UPRN', value: text(p.uprn) || 'None in the file' });
  if (text(p.supplierReason)) rows.push({ label: "The supplier's reason", value: text(p.supplierReason) });
  if (text(p.statusChangedOn)) rows.push({ label: 'Sale status changed on', value: dayOf(text(p.statusChangedOn)) || text(p.statusChangedOn) });
  if (text(p.councilDistrict)) rows.push({ label: 'Council district', value: text(p.councilDistrict) });
  if (p.importRow !== null && p.importRow !== undefined && text(p.importRow)) rows.push({ label: 'Row of the file', value: text(p.importRow) });
  const also = (Array.isArray(p.alsoListedAs) ? p.alsoListedAs : []).map(text).filter(Boolean);
  if (also.length) rows.push({ label: 'Also listed as', value: also.join(', ') });
  const parts = [
    ['Flat or unit', p.saon], ['Building number', p.buildingNumber], ['Building name', p.buildingName],
    ['Street', p.street], ['Locality', p.locality], ['Town', p.town], ['Postcode', p.postcode],
  ].map(([label, value]) => ({ label, value: text(value) })).filter((part) => part.value);
  return { made: true, rows, parts };
}

/** The imports that only LINKED a record of ours (their ids), for a line on the home's page. */
export function linkedImportsOf(property) {
  const p = property || {};
  const all = (Array.isArray(p.importIds) ? p.importIds : []).map(text).filter(Boolean);
  return all.filter((id) => id !== text(p.importId));
}
